import { useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';
import { cn } from './cn';

interface RevealProps {
  children: ReactNode;
  /** Delay in ms — use `index * 70` to stagger siblings. */
  delay?: number;
  as?: ElementType;
  className?: string;
  id?: string;
}

/**
 * Fades and lifts its content into view the first time it scrolls on screen.
 * Falls back to "visible" where IntersectionObserver is missing (old browsers, tests),
 * and the global reduced-motion rule in index.css turns the movement off.
 */
export function Reveal({ children, delay = 0, as: Tag = 'div', className, ...rest }: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  return (
    <Tag ref={ref} className={cn('reveal', shown && 'is-in', className)} style={{ '--reveal-delay': `${delay}ms` } as CSSProperties} {...rest}>
      {children}
    </Tag>
  );
}
